import { Grid } from "@mui/material";
import { FormDatePicker, FormTextField } from "components/atoms";
import { DetailTableTabs, DocumentDetailHeader } from "components/molecules";
import { FormikHelpers, useFormik } from "formik";
import { StatusHelper } from "helpers";
import { useRequest } from "hooks/useRequest";
import {
  EDeliveryStatus,
  IDeliveryOrder,
  IGetByIdRequest,
  IUpdateDeliveryOrderRequest,
} from "model";

import React, { useState } from "react";
import { useParams } from "react-router-dom";
import { DeliveryOrderActions } from "redux/actions";
import {
  useAppDispatch,
  useAppSelector,
} from "redux/configurations/reduxHooks";
import DeliveryOrderApiService from "services/DeliveryOrderApiService";
import { I18nTranslator, translations } from "utils/i18n";
import { PrepareDocumentList, ProductList } from "./components";

const DeliveryOrderDetail = () => {
  const params = useParams<{
    id: string;
    deliveryCode: string;
  }>();

  const tabsName = [
    I18nTranslator(translations.receive.productList),
    I18nTranslator(translations.receive.prepareDocumentList),
  ];

  const tabsPanel = [
    <ProductList deliveryOrderHeaderId={params.id} />,
    <PrepareDocumentList deliveryTicketHeaderId={params.id} />,
  ];

  return (
    <>
      <DetailHeader />
      <DetailTableTabs
        hideHeaderButton
        tabsName={tabsName}
        tabsPanel={tabsPanel}
      />
    </>
  );
};

const DetailHeader = () => {
  const params = useParams<{
    id: string;
    deliveryCode: string;
  }>();

  const dispatch = useAppDispatch();
  const action = useAppSelector((state) => state.ReceiveReducer.action);
  const [request] = useState({ id: params.id });
  const { loading, data } = useRequest<IGetByIdRequest, IDeliveryOrder>(
    request,
    DeliveryOrderApiService.getDeliveryOrderById
  );

  const formik = useFormik<IDeliveryOrder>({
    initialValues: data || ({} as IDeliveryOrder),
    enableReinitialize: true,
    onSubmit: (values: any, { validateForm }: FormikHelpers<any>) => {
      validateForm().then(() => {
        const request: IUpdateDeliveryOrderRequest = {
          ...values,
        };
        dispatch(DeliveryOrderActions.updateDeliveryOrder.request(request));
      });
    },
  });

  return (
    <DocumentDetailHeader
      title={I18nTranslator(translations.receive.deliveryDetail)}
      description={params.deliveryCode}
      formik={formik}
      loading={false}
    >
      <Grid container xs={12} item columnSpacing={2}>
        <Grid xs={6} item>
          <FormTextField
            label={I18nTranslator(translations.receive.companyCode)}
            placeholder={"Nhập tên nhà cung cấp"}
            id="companyCode"
            disabled
          />
        </Grid>
        <Grid xs={6} item>
          <FormDatePicker
            label={I18nTranslator(translations.receive.deliveryDate)}
            id="deliveryDate"
            disabled
          />
        </Grid>
      </Grid>
      <Grid container xs={12} item columnSpacing={2}>
        <Grid xs={6} item>
          <FormTextField
            label={I18nTranslator(translations.receive.companyName)}
            placeholder={"Nhập tên nhà cung cấp"}
            id="companyName"
            disabled
          />
        </Grid>
        <Grid xs={6} item>
          <FormTextField
            label={I18nTranslator(translations.status)}
            id="status"
            disabled
            renderValue={() =>
              StatusHelper.getDeliveryStatusString(
                formik.values.status! as EDeliveryStatus
              )
            }
          />
        </Grid>
      </Grid>
      <Grid container xs={12} item columnSpacing={2}>
        <Grid xs={6} item>
          <FormTextField
            label={I18nTranslator(translations.receive.country)}
            placeholder={"Nhập tên nhà cung cấp"}
            id="province"
            disabled
          />
        </Grid>
        <Grid xs={6} item>
          <FormTextField
            label={I18nTranslator(translations.receive.totalWeight)}
            id="grossWeight"
            type={"number"}
          />
        </Grid>
      </Grid>
      <Grid container xs={12} item columnSpacing={2}>
        <Grid xs={6} item>
          <FormTextField
            label={I18nTranslator(translations.receive.deliveryAddress)}
            placeholder={I18nTranslator(
              translations.placeholder.deliveryAddress
            )}
            id="deliveryAddress"
          />
        </Grid>
        <Grid xs={6} item>
          <FormTextField
            label={I18nTranslator(translations.receive.totalSaleWeight)}
            placeholder={I18nTranslator(
              translations.placeholder.totalSaleWeight
            )}
            id="freeItemsWeight"
            type={"number"}
          />
        </Grid>
      </Grid>
      <Grid container xs={12} item columnSpacing={2}>
        <Grid xs={6} item>
          <FormTextField
            label={I18nTranslator(translations.receive.orderNumber)}
            placeholder={I18nTranslator(translations.placeholder.orderNumber)}
            id="listOrderNumbers"
          />
        </Grid>
        <Grid xs={6} item>
          <FormTextField
            label={I18nTranslator(translations.receive.truckNo)}
            placeholder={I18nTranslator(translations.placeholder.truckNo)}
            id="truckNo"
          />
        </Grid>
      </Grid>
      <Grid container xs={12} item columnSpacing={2}>
        <Grid xs={6} item>
          <FormTextField
            label={I18nTranslator(translations.receive.saleOff)}
            placeholder={I18nTranslator(translations.placeholder.saleOff)}
            id="listFreeItems"
          />
        </Grid>
        <Grid xs={6} item>
          <FormTextField
            label={I18nTranslator(translations.receive.driver)}
            placeholder={I18nTranslator(translations.placeholder.driver)}
            id="driver"
          />
        </Grid>
      </Grid>
      <Grid container xs={12} item columnSpacing={2}>
        <Grid xs={6} item>
          <FormTextField
            label={I18nTranslator(translations.receive.deliverUnit)}
            placeholder={I18nTranslator(translations.placeholder.deliveryUnit)}
            id="deliveryUOMSum"
          />
        </Grid>
        <Grid xs={6} item>
          <FormTextField
            label={I18nTranslator(translations.receive.truckSupplier)}
            placeholder={I18nTranslator(translations.placeholder.truckSupplier)}
            id="truckSupplier"
          />
        </Grid>
      </Grid>
      <Grid container xs={12} item columnSpacing={2}>
        <Grid xs={6} item>
          <FormTextField
            label={I18nTranslator(translations.receive.receiptBy)}
            placeholder={I18nTranslator(translations.placeholder.receiptBy)}
            id="receiptBy"
          />
        </Grid>
        <Grid xs={6} item>
          <FormTextField
            label={I18nTranslator(translations.receive.deliver)}
            placeholder={I18nTranslator(translations.placeholder.deliver)}
            id="deliveverSign"
          />
        </Grid>
      </Grid>
      <Grid xs={12} item>
        <FormTextField
          label={I18nTranslator(translations.receive.description)}
          placeholder={I18nTranslator(translations.placeholder.description)}
          id="description"
        />
      </Grid>
    </DocumentDetailHeader>
  );
};

export default DeliveryOrderDetail;
